import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import {
  CATEGORIES_SAISIE,
  chargesFixesDuCamion,
  chargesPrevues,
  cleFrais,
  creerChargeFixe,
  messageErreurFrais,
  modifierChargeFixe,
  optionCompte,
  payerChargeFixe,
  supprimerChargeFixe,
} from '../../lib/expensesApi';
import { formatDate, formatMois } from '../../lib/formats';
import { libelle, optionsLibelles } from '../../lib/libelles';
import { formatCurrency } from '../../lib/utils';
import ExpenseForm from '../finance/ExpenseForm';
import ExpensesTable from '../finance/ExpensesTable';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { FieldError } from '../ui/field-error';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SearchableSelect } from '../ui/searchable-select';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../ui/table';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';

/**
 * Onglet « Frais » de la fiche camion.
 *
 * Étape du parcours: après le chargement et la vente, ce que le camion a coûté.
 * De haut en bas, comme le classeur:
 *  1. les charges fixes du mois (salaire…): « prévu » ou « payé », avec le
 *     bouton « Payer » (direction seulement, ce sont des montants de rentabilité);
 *  2. la saisie d'un frais;
 *  3. le tableau des frais du camion, mois par mois.
 *
 * L'assurance ne se saisit pas ici: elle vient des papiers du camion; le loyer
 * vient du contrat de location.
 */

const champ = 'mt-1 block w-full rounded-md border bg-background p-2 text-sm';

const moisCourant = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const aujourdhui = () => `${moisCourant()}-${String(new Date().getDate()).padStart(2, '0')}`;

const regleVide = () => ({
  category: 'SALAIRE',
  label: '',
  monthly_amount: '',
  start_date: aujourdhui(),
  end_date: '',
  cost_bearer: 'SOCIETE',
  default_account_id: '',
});

function ChargesFixes({ truck, comptes, onChanged }) {
  const [mois, setMois] = useState(moisCourant());
  const [attendues, setAttendues] = useState([]);
  const [regles, setRegles] = useState([]);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(true);
  const [paiement, setPaiement] = useState(null);
  const [saisieRegle, setSaisieRegle] = useState(null);
  const [erreursRegle, setErreursRegle] = useState({});
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      const [prevues, liste] = await Promise.all([
        chargesPrevues({ month: mois, truckIds: [truck.id] }),
        chargesFixesDuCamion(truck.id),
      ]);
      setAttendues(prevues);
      setRegles(liste);
    } catch (error) {
      setErreur(messageErreurFrais(error, 'Les charges fixes n’ont pas pu être chargées.'));
    } finally {
      setChargement(false);
    }
  }, [mois, truck.id]);

  useEffect(() => {
    charger();
  }, [charger]);
  useRafraichirSur(['depenses', 'camions', 'finance'], charger);

  const reglesParId = useMemo(() => new Map(regles.map((r) => [r.id, r])), [regles]);
  const comptesOptions = useMemo(() => comptes.map(optionCompte), [comptes]);
  const totalPrevu = attendues.reduce((somme, l) => somme + (Number(l.planned_amount) || 0), 0);
  const totalPaye = attendues.filter((l) => l.paid).reduce((somme, l) => somme + (Number(l.amount) || 0), 0);

  const ouvrirPaiement = (ligne) =>
    setPaiement({
      ligne,
      amount: String(ligne.planned_amount ?? ligne.amount ?? ''),
      account_id: reglesParId.get(ligne.rule_id)?.default_account_id || '',
      paid_at: aujourdhui(),
      erreur: '',
    });

  const confirmerPaiement = async () => {
    if (!paiement.account_id) {
      setPaiement((p) => ({ ...p, erreur: 'Choisissez la caisse ou le compte qui paie.' }));
      return;
    }
    if (!(Number(paiement.amount) > 0)) {
      setPaiement((p) => ({ ...p, erreur: 'Indiquez un montant supérieur à zéro.' }));
      return;
    }
    setOccupe(true);
    try {
      await payerChargeFixe(paiement.ligne.rule_id, {
        month: mois,
        amount: Number(paiement.amount),
        account_id: paiement.account_id,
        paid_at: paiement.paid_at || undefined,
      });
      setPaiement(null);
      await charger();
      if (onChanged) onChanged();
    } catch (error) {
      setPaiement((p) => ({ ...p, erreur: messageErreurFrais(error, 'Le paiement n’a pas pu être enregistré.') }));
    } finally {
      setOccupe(false);
    }
  };

  const enregistrerRegle = async () => {
    const e = {};
    if (!(Number(saisieRegle.monthly_amount) > 0)) e.monthly_amount = 'Indiquez un montant mensuel supérieur à zéro.';
    if (!saisieRegle.start_date) e.start_date = 'Indiquez la date de début.';
    setErreursRegle(e);
    if (Object.keys(e).length) return;
    setOccupe(true);
    try {
      await creerChargeFixe(truck.id, {
        category: saisieRegle.category,
        label: saisieRegle.label.trim() || null,
        monthly_amount: Number(saisieRegle.monthly_amount),
        start_date: saisieRegle.start_date,
        end_date: saisieRegle.end_date || null,
        cost_bearer: saisieRegle.cost_bearer,
        default_account_id: saisieRegle.default_account_id || null,
        idempotency_key: cleFrais('charge'),
      });
      setSaisieRegle(null);
      await charger();
    } catch (error) {
      setErreursRegle({ global: messageErreurFrais(error, 'La charge n’a pas pu être enregistrée.') });
    } finally {
      setOccupe(false);
    }
  };

  const basculer = async (regle) => {
    setOccupe(true);
    setErreur('');
    try {
      await modifierChargeFixe(regle.id, { is_active: !regle.is_active });
      await charger();
    } catch (error) {
      setErreur(messageErreurFrais(error));
    } finally {
      setOccupe(false);
    }
  };

  const retirer = async (regle) => {
    if (!window.confirm('Supprimer cette charge fixe ?')) return;
    setOccupe(true);
    setErreur('');
    try {
      await supprimerChargeFixe(regle.id);
      await charger();
    } catch (error) {
      setErreur(messageErreurFrais(error));
    } finally {
      setOccupe(false);
    }
  };

  return (
    <section className="space-y-4 rounded-xl border bg-card p-4" aria-label="Charges fixes">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-1">
            <h3 className="text-base font-semibold">Charges fixes du mois</h3>
            <Aide titre="Charges fixes du mois">Salaire et autres charges qui reviennent chaque mois. Tant qu’un mois n’est pas payé, le montant reste « prévu ».</Aide>
          </div>
        </div>
        <div>
          <Label htmlFor="charges-mois">Mois</Label>
          <Input id="charges-mois" type="month" className="mt-1" value={mois} onChange={(event) => event.target.value && setMois(event.target.value)} />
        </div>
      </div>

      {erreur && (
        <p role="alert" className="text-sm text-destructive">
          {erreur}
        </p>
      )}

      {chargement ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : attendues.length === 0 ? (
        <p className="flex items-center gap-1 rounded-md border border-dashed p-4 text-sm text-muted-foreground">
          Aucune charge fixe pour {formatMois(mois)}.
          <Aide titre="Charges fixes">Ajoutez le salaire du chauffeur ou une autre charge mensuelle ci-dessous.</Aide>
        </p>
      ) : (
        <Table stickyFirstColumn={false}>
          <TableHeader>
            <TableRow>
              <TableHead>Charge</TableHead>
              <TableHead>Catégorie</TableHead>
              <TableHead>État</TableHead>
              <TableHead className="text-right">Prévu</TableHead>
              <TableHead className="text-right">Payé</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {attendues.map((ligne) => (
              <TableRow key={ligne.rule_id} data-testid={`charge-${ligne.rule_id}`}>
                <TableCell>{ligne.label || libelle('categorie_frais', ligne.category)}</TableCell>
                <TableCell>{libelle('categorie_frais', ligne.category)}</TableCell>
                <TableCell>
                  <Badge variant={ligne.paid ? 'success' : 'warning'}>{ligne.paid ? 'Payé' : 'Prévu'}</Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatCurrency(ligne.planned_amount)}</TableCell>
                <TableCell className="text-right tabular-nums">{ligne.paid ? formatCurrency(ligne.amount) : '—'}</TableCell>
                <TableCell className="text-right">
                  {!ligne.paid && (
                    <Button type="button" size="sm" disabled={occupe} onClick={() => ouvrirPaiement(ligne)}>
                      Payer
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3} className="font-semibold">
                Total de {formatMois(mois)}
              </TableCell>
              <TableCell className="text-right font-semibold tabular-nums">{formatCurrency(totalPrevu)}</TableCell>
              <TableCell className="text-right font-semibold tabular-nums">{formatCurrency(totalPaye)}</TableCell>
              <TableCell />
            </TableRow>
          </TableFooter>
        </Table>
      )}

      <div className="space-y-3 rounded-lg border p-3">
        <p className="text-sm font-medium">Charges fixes de ce camion ({regles.length})</p>
          {regles.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune charge fixe enregistrée pour ce camion.</p>
          ) : (
            <Table stickyFirstColumn={false}>
              <TableHeader>
                <TableRow>
                  <TableHead>Charge</TableHead>
                  <TableHead className="text-right">Par mois</TableHead>
                  <TableHead>Début</TableHead>
                  <TableHead>Fin</TableHead>
                  <TableHead>Supportée par</TableHead>
                  <TableHead>État</TableHead>
                  <TableHead className="w-40" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {regles.map((regle) => (
                  <TableRow key={regle.id}>
                    <TableCell>{regle.label || libelle('categorie_frais', regle.category)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(regle.monthly_amount)}</TableCell>
                    <TableCell>{formatDate(regle.start_date)}</TableCell>
                    <TableCell>{regle.end_date ? formatDate(regle.end_date) : 'Sans fin'}</TableCell>
                    <TableCell>{libelle('porteur_frais', regle.cost_bearer || 'SOCIETE')}</TableCell>
                    <TableCell>
                      <Badge variant={regle.is_active ? 'success' : 'muted'}>{regle.is_active ? 'En cours' : 'Arrêtée'}</Badge>
                    </TableCell>
                    <TableCell className="space-x-1 text-right">
                      <Button type="button" variant="ghost" size="sm" disabled={occupe} onClick={() => basculer(regle)}>
                        {regle.is_active ? 'Arrêter' : 'Reprendre'}
                      </Button>
                      <Button type="button" variant="ghost" size="sm" disabled={occupe} onClick={() => retirer(regle)}>
                        Supprimer
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {saisieRegle ? (
            <div className="space-y-3 rounded-md border bg-background p-3">
              <p className="text-sm font-medium">Nouvelle charge fixe</p>
              {erreursRegle.global && (
                <p role="alert" className="text-sm text-destructive">
                  {erreursRegle.global}
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="regle-categorie">Catégorie</Label>
                  <select
                    id="regle-categorie"
                    className={champ}
                    value={saisieRegle.category}
                    onChange={(event) => setSaisieRegle((r) => ({ ...r, category: event.target.value }))}
                  >
                    {optionsLibelles('categorie_frais', CATEGORIES_SAISIE.filter((c) => c !== 'FRAIS_SUPP')).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="regle-nom">Nom (facultatif)</Label>
                  <Input
                    id="regle-nom"
                    className="mt-1"
                    placeholder="Ex. salaire du chauffeur"
                    value={saisieRegle.label}
                    onChange={(event) => setSaisieRegle((r) => ({ ...r, label: event.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="regle-montant">Montant par mois (FCFA)</Label>
                  <Input
                    id="regle-montant"
                    type="number"
                    min="1"
                    className="mt-1 text-right"
                    value={saisieRegle.monthly_amount}
                    onChange={(event) => setSaisieRegle((r) => ({ ...r, monthly_amount: event.target.value }))}
                    aria-invalid={Boolean(erreursRegle.monthly_amount)}
                  />
                  <FieldError show={Boolean(erreursRegle.monthly_amount)}>{erreursRegle.monthly_amount}</FieldError>
                </div>
                <div>
                  <Label htmlFor="regle-porteur">Qui supporte cette charge</Label>
                  <select
                    id="regle-porteur"
                    className={champ}
                    value={saisieRegle.cost_bearer}
                    onChange={(event) => setSaisieRegle((r) => ({ ...r, cost_bearer: event.target.value }))}
                  >
                    {['SOCIETE', 'PROPRIETAIRE'].map((code) => (
                      <option key={code} value={code}>
                        {libelle('porteur_frais', code)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="regle-debut">À partir du</Label>
                  <Input
                    id="regle-debut"
                    type="date"
                    className="mt-1"
                    value={saisieRegle.start_date}
                    onChange={(event) => setSaisieRegle((r) => ({ ...r, start_date: event.target.value }))}
                    aria-invalid={Boolean(erreursRegle.start_date)}
                  />
                  <FieldError show={Boolean(erreursRegle.start_date)}>{erreursRegle.start_date}</FieldError>
                </div>
                <div>
                  <Label htmlFor="regle-fin">Jusqu’au (facultatif)</Label>
                  <Input
                    id="regle-fin"
                    type="date"
                    className="mt-1"
                    value={saisieRegle.end_date}
                    onChange={(event) => setSaisieRegle((r) => ({ ...r, end_date: event.target.value }))}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="regle-compte">Compte habituel de paiement (facultatif)</Label>
                  <SearchableSelect
                    id="regle-compte"
                    aria-label="Compte habituel de paiement"
                    className="mt-1 rounded-md border bg-background p-2"
                    value={saisieRegle.default_account_id}
                    onValueChange={(valeur) => setSaisieRegle((r) => ({ ...r, default_account_id: valeur }))}
                    options={comptesOptions}
                    placeholder="Choisir un compte"
                    searchPlaceholder="Rechercher un compte…"
                    emptyMessage="Aucun compte trouvé"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setSaisieRegle(null)}>
                  Annuler
                </Button>
                <Button type="button" size="sm" disabled={occupe} onClick={enregistrerRegle}>
                  {occupe ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <Plus className="mr-2 h-4 w-4" aria-hidden="true" />}
                  Enregistrer la charge
                </Button>
              </div>
            </div>
          ) : (
            <Button type="button" variant="outline" size="sm" onClick={() => { setSaisieRegle(regleVide()); setErreursRegle({}); }}>
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              Ajouter une charge fixe
            </Button>
          )}
      </div>

      <Dialog open={Boolean(paiement)} onOpenChange={(ouvert) => !ouvert && setPaiement(null)}>
        <DialogContent className="sm:max-w-md">
          {paiement && (
            <>
              <DialogHeader>
                <DialogTitle>Payer {paiement.ligne.label || libelle('categorie_frais', paiement.ligne.category)}</DialogTitle>
                <DialogDescription>
                  Mois de {formatMois(mois)}. Le paiement s’ajoute aux frais du camion.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {paiement.erreur && (
                  <p role="alert" className="text-sm text-destructive">
                    {paiement.erreur}
                  </p>
                )}
                <div>
                  <Label htmlFor="paiement-montant">Montant payé (FCFA)</Label>
                  <Input
                    id="paiement-montant"
                    type="number"
                    min="1"
                    className="mt-1 text-right"
                    value={paiement.amount}
                    onChange={(event) => setPaiement((p) => ({ ...p, amount: event.target.value, erreur: '' }))}
                  />
                </div>
                <div>
                  <Label htmlFor="paiement-compte">Payé depuis</Label>
                  <SearchableSelect
                    id="paiement-compte"
                    aria-label="Payé depuis"
                    className="mt-1 rounded-md border bg-background p-2"
                    value={paiement.account_id}
                    onValueChange={(valeur) => setPaiement((p) => ({ ...p, account_id: valeur, erreur: '' }))}
                    options={comptesOptions}
                    placeholder="Choisir la caisse ou le compte"
                    searchPlaceholder="Rechercher un compte…"
                    emptyMessage="Aucun compte trouvé"
                  />
                </div>
                <div>
                  <Label htmlFor="paiement-date">Date du paiement</Label>
                  <Input
                    id="paiement-date"
                    type="date"
                    className="mt-1"
                    value={paiement.paid_at}
                    onChange={(event) => setPaiement((p) => ({ ...p, paid_at: event.target.value }))}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPaiement(null)}>
                  Annuler
                </Button>
                <Button type="button" disabled={occupe} onClick={confirmerPaiement}>
                  {occupe ? 'Enregistrement…' : 'Confirmer le paiement'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default function TruckExpensesTab({ truck }) {
  const { canSeeMoney } = useAuth();
  const [version, setVersion] = useState(0);
  const [saisieOuverte, setSaisieOuverte] = useState(false);
  const [comptes, setComptes] = useState([]);

  useEffect(() => {
    let actif = true;
    if (!canSeeMoney) return undefined;
    api
      .get('/finance/accounts')
      .then((r) => actif && setComptes(r?.data?.data?.items || r?.data?.data || r?.data || []))
      .catch(() => actif && setComptes([]));
    return () => {
      actif = false;
    };
  }, [canSeeMoney]);
  // Un frais saisi ailleurs (Finances, trajet, autre onglet) recharge les listes.
  useRafraichirSur(['depenses', 'finance', 'trajets'], () => setVersion((v) => v + 1));

  if (!truck?.id) return null;
  const recharger = () => setVersion((v) => v + 1);

  return (
    <div className="space-y-6" data-testid="truck-expenses-tab">
      {canSeeMoney && <ChargesFixes truck={truck} comptes={comptes} onChanged={recharger} />}

      <section className="space-y-3" aria-label="Frais du camion">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-1">
              <h3 className="text-base font-semibold">Frais du camion</h3>
              <Aide titre="Frais du camion">Tous les frais payés pour ce camion, mois par mois : frais de route, entretien, pièces, frais supplémentaires.</Aide>
            </div>
          </div>
          <Button type="button" variant={saisieOuverte ? 'outline' : 'default'} size="sm" onClick={() => setSaisieOuverte((o) => !o)}>
            {saisieOuverte ? 'Fermer la saisie' : (
              <>
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Ajouter un frais
              </>
            )}
          </Button>
        </div>
        {saisieOuverte && <ExpenseForm truckId={truck.id} onSaved={recharger} />}
        <ExpensesTable truckId={truck.id} parMois refreshKey={version} />
      </section>
    </div>
  );
}
